/* eslint-disable react/forbid-prop-types */
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { ContentZonesService } from '@sm360/widget-commons-core-services';
import { AnalyticsService, TRACKING_TAGS as TAGS, ADOBE_TAGS, AdobeService } from '@sm360/widget-commons-general';
import Quote from 'commons-vehicle/Quote/models/QuoteModel.js';
import AppointmentCTA from 'commons-vehicle/Vehicle/components/CallToAction/Appointment/AppointmentCTA.jsx';
import AppointmentCTAV25 from 'commons-vehicle/Vehicle/components/CallToAction/Appointment/AppointmentCTAV25.jsx';
import BuyOnlineCTA from 'commons-vehicle/Vehicle/components/CallToAction/BuyOnline/BuyOnlineCTA.jsx';
import BuyOnlineCTAV25 from 'commons-vehicle/Vehicle/components/CallToAction/BuyOnline/BuyOnlineCTAV25.jsx';
import VehicleSummary from 'commons-vehicle/Vehicle/components/Overview/VehicleSummary/VehicleSummary.jsx';
import URL from 'commons-vehicle/Vehicle/constants/url.js';
import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';
import VehicleModelV25 from 'commons-vehicle/Vehicle/models/VehicleModelV25.js';

import { ImageWithFallback } from '@sm360/widget-commons-basic-components';
import envConfig from 'commons-vehicle/config/index.js';

const CONTENT_ZONE_NAME = {
    LISTING: 'widget.listing.used',
    VDP: 'widget.vdp.used.similar.cars',
};

const getFallbackImageUrl = () => {
    const defaultPicture = 'default.picture';
    let contentZoneDefaultPicture = ContentZonesService.getContentZone(CONTENT_ZONE_NAME.VDP, defaultPicture);
    if (!contentZoneDefaultPicture || !contentZoneDefaultPicture.getValue()) {
        contentZoneDefaultPicture = ContentZonesService.getContentZone(CONTENT_ZONE_NAME.LISTING, defaultPicture);
    }
    return (
        (contentZoneDefaultPicture && contentZoneDefaultPicture.getValue()) ||
        `${envConfig.source.image}/ir/w360h197c/images/${URL.NO_PHOTO}`
    );
};

const PaymentSummary = ({
    imageUrl,
    isUsed,
    isReserved,
    isSold,
    optionalButton,
    optionalBuyCta,
    optionalAppointmentCta,
    packageName,
    purchaseMethods,
    quote,
    taxCodes,
    taxesDisclaimer,
    title,
    includedDealerIncentives,
    hasSDInitialClickSent,
    onSetSDInitialClickEvent,
    displayAppointmentCta,
    config,
    hasAdobeTagging,
    vehicle,
    displayCreditRate,
}) => {
    const trackBuyOnlineCta = () => {
        const tag = isUsed ? TAGS.RESERVE_IT_NOW_CTA_USED : TAGS.RESERVE_IT_NOW_CTA_NEW;
        AnalyticsService.gaTrigger(tag(quote.vehicle.getModel()));
        if (hasAdobeTagging) {
            AdobeService.eventTrigger(
                ADOBE_TAGS.VDP_ACTIONS(
                    quote?.vehicle?.vehicleCondition,
                    'buy online',
                    {
                        make: quote?.vehicle?.make?.name,
                        model: quote?.vehicle?.model?.name,
                    },
                    vehicle?.commercialVehicle
                ),
                'global-onclick'
            );
        }
        // Only send initial click event once on page DR tool interaction
        if (!hasSDInitialClickSent) onSetSDInitialClickEvent();
    };

    const trackBuyOnlineCtaOnEndProcessGaTag = isUsed
        ? TAGS.RESERVE_IT_NOW_PROCESS_ENDED_USED(quote.vehicle?.getModel())
        : TAGS.RESERVE_IT_NOW_PROCESS_ENDED_NEW(quote.vehicle?.getModel());

    const trackAppointmentCta = () => {
        const tag = isUsed ? TAGS.PREPARE_YOUR_VISIT_CTA_USED : TAGS.PREPARE_YOUR_VISIT_CTA_NEW;
        AnalyticsService.gaTrigger(tag(quote.vehicle.getModel()));
        // Only send initial click event once on page DR tool interaction
        if (!hasSDInitialClickSent) onSetSDInitialClickEvent();
    };

    const appointmentCtaOnEndProcessGaTag = isUsed
        ? TAGS.PREPARE_YOUR_VISIT_PROCESS_ENDED_USED(quote.vehicle?.getModel())
        : TAGS.PREPARE_YOUR_VISIT_PROCESS_ENDED_NEW(quote.vehicle?.getModel());

    const ctaBlock =
        !isReserved &&
        !isSold &&
        (quote.vehicle?.vehicleId !== 0 ? (
            <div>
                <BuyOnlineCTAV25
                    quote={quote}
                    buttonClass="sr-button-1 is-fluid"
                    alternativeLabel={optionalBuyCta}
                    onClick={trackBuyOnlineCta}
                    onEndProcessGaTag={trackBuyOnlineCtaOnEndProcessGaTag}
                    config={config}
                    hasAdobeTagging={hasAdobeTagging}
                />
                {!!displayAppointmentCta && (
                    <AppointmentCTAV25
                        quote={quote}
                        buttonClass="sr-button-2 is-fluid"
                        alternativeLabel={optionalAppointmentCta}
                        onClick={trackAppointmentCta}
                        onEndProcessGaTag={appointmentCtaOnEndProcessGaTag}
                        config={config}
                        hasAdobeTagging={hasAdobeTagging}
                        vehicle={vehicle}
                    />
                )}
            </div>
        ) : (
            <div>
                <BuyOnlineCTA
                    quote={quote}
                    buttonClass="sr-button-1 is-fluid"
                    alternativeLabel={optionalBuyCta}
                    onClick={trackBuyOnlineCta}
                    onEndProcessGaTag={trackBuyOnlineCtaOnEndProcessGaTag}
                />
                <AppointmentCTA
                    quote={quote}
                    buttonClass="sr-button-2 is-fluid"
                    alternativeLabel={optionalAppointmentCta}
                    onClick={trackAppointmentCta}
                    onEndProcessGaTag={appointmentCtaOnEndProcessGaTag}
                />
            </div>
        ));

    return (
        <div className="vehicle-overview__summary has-primary-background">
            <div className="vehicle-overview__image">
                <ImageWithFallback alt={title} imageUrl={imageUrl} fallbackImageUrl={getFallbackImageUrl()} />
            </div>
            <VehicleSummary
                title={title}
                packageName={packageName}
                purchaseMethods={purchaseMethods}
                taxCodes={taxCodes}
                taxesDisclaimer={taxesDisclaimer}
                isUsed={isUsed}
                vehicle={quote.vehicle}
                includedDealerIncentives={includedDealerIncentives}
                config={config}
                displayCreditRate={displayCreditRate}
            />
            <div className="vehicle-overview__cta">{ctaBlock}</div>
            <div className="vehicle-overview__optionalButton">{optionalButton}</div>
        </div>
    );
};

PaymentSummary.defaultProps = {
    imageUrl: 'https://img.sm360.ca/ir/w420h280c/images/widget/showroom/asset/non-photo.png',
    isUsed: false,
    isReserved: false,
    isSold: false,
    optionalButton: noop,
    optionalBuyCta: '',
    optionalAppointmentCta: '',
    packageName: '',
    quote: '',
    includedDealerIncentives: false,
    purchaseMethods: new PurchaseMethodsModel(),
    taxesDisclaimer: '',
    displayAppointmentCta: true,
    hasAdobeTagging: false,
    vehicle: new VehicleModelV25(),
    displayCreditRate: false,
};

PaymentSummary.propTypes = {
    imageUrl: PropTypes.string,
    isUsed: PropTypes.bool,
    isReserved: PropTypes.bool,
    isSold: PropTypes.bool,
    optionalButton: PropTypes.func,
    optionalBuyCta: PropTypes.string,
    optionalAppointmentCta: PropTypes.string,
    packageName: PropTypes.string,
    purchaseMethods: PropTypes.shape({
        cashPurchase: PropTypes.object.isRequired,
        financing: PropTypes.object.isRequired,
    }),
    quote: PropTypes.instanceOf(Quote.QuoteModel),
    taxCodes: PropTypes.string.isRequired,
    taxesDisclaimer: PropTypes.string,
    title: PropTypes.string.isRequired,
    includedDealerIncentives: PropTypes.bool,
    displayAppointmentCta: PropTypes.bool,
    hasAdobeTagging: PropTypes.bool,
    vehicle: PropTypes.instanceOf(VehicleModelV25),
    displayCreditRate: PropTypes.bool,
};

PaymentSummary.displayName = 'PaymentSummary';

export default PaymentSummary;
